/**
 * MUI Ceiling component that replaces the styled-components Ceiling component
 * 
 * This component provides a Material UI equivalent of the original Ceiling component
 * with similar props and functionality.
 */
import React, { useEffect, useState, useRef, useContext, Fragment, useLayoutEffect } from "react";
import { styled } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import ReducersIndex from 'reduxModules/ducks';
import { getConditionResult, getCurrentValue2 } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { getTileQuantities } from "reduxModules/calculations/roomCalculations";
import { Move } from "globalConstants/Icons/Actions";
import ToolContext, { ToolContextType } from "components/services/ToolContext";
import useMousePosition from "hooks/useMousePosition";
import { mapValues, round, findIndex } from "lodash";
import { getFloorplanBoundaries } from "utilities/floorplanBoundary";
import { 
  MuiCeilingProps,
  TileCenter,
  CeilingTransform,
  IconTransform,
  MoveLimits,
  TileChecked,
  OffsetPosition,
  CeilingLineProps,
  CeilingCrossProps,
  CeilingMoveProps
} from "types/components/elements";
import { RootState } from "types/redux/state";
import { micInCategory } from "types/models/microphone";

//  Previously this file defined its own getTileQuantities selector with a different
// formula (qty + 2 buffer, no clamp to maxX). It produced drawTileQty values one tile larger
// than the canonical selector in roomCalculations.ts, which caused the tile grid in MuiCeiling
// to be drawn shifted by half a tile from the tile-center coordinates the snap logic targets.
// Result: auto-placed mics/speakers landed exactly on the visible tile lines (crosspoints)
// instead of at tile centers. Now uses the canonical selector so both sides agree.

/**
 * Returns the local (x, y) center, in pixels relative to the ceiling group origin,
 * of the rightmost / topmost LOCAL tile whose bounds fall inside the room.
 *
 * Used for the grid-move icon. Pinning the icon to a fixed local tile (e.g. the
 * top-right inner tile of the un-dragged grid) sent the icon outside the canvas
 * once the user dragged the grid by ~1 tile, because the icon's local tile then
 * sat outside the room. Re-anchoring against the current `tx`/`ty` keeps the
 * icon visible at the room's top-right corner as the grid slides. See PIG-209.
 */
const computeIconAnchor = (
  tx: number,
  ty: number,
  tileSize: Record<string, number>,
  drawQtyX: number,
  drawQtyY: number,
  bbox: { x0: number; y0: number; width: number; height: number },
): { x: number; y: number } => {
  const tileX_size = tileSize.x ?? 0;
  const tileY_size = tileSize.y ?? 0;
  if (!tileX_size || !tileY_size || drawQtyX <= 0 || drawQtyY <= 0) {
    return { x: 0, y: 0 };
  }
  // Rightmost local tile whose right edge is still inside the room.
  let tileX = 0;
  for (let i = drawQtyX - 1; i >= 0; i--) {
    if (tx + (i + 1) * tileX_size <= bbox.x0 + bbox.width) { tileX = i; break; }
  }
  // Topmost local tile whose top edge is still inside the room.
  let tileY = 0;
  for (let i = 0; i < drawQtyY; i++) {
    if (ty + i * tileY_size >= bbox.y0) { tileY = i; break; }
  }
  return {
    x: tileX * tileX_size + tileX_size / 2,
    y: tileY * tileY_size + tileY_size / 2,
  };
};

const getMicrophoneInfo = createSelector(
  (state: RootState) => {
    const areaId = getCurrentValue2(state, 'activeTab');
    return state.container.areas[areaId]?.microphones;
  },
  (microphones) => ({
    model: (microphones?.model ?? '') as string,
    layout: (microphones?.layout ?? []) as any[],
  })
);

/**
 * MUI Ceiling component
 */
const MuiCeiling: React.FC<MuiCeilingProps> = (props) => {
  const areaId = useSelector((state: RootState) => getCurrentValue2(state, 'activeTab'));
  const { bbox, scale } = useSelector(getDrawingBox);
  const currentTiles = useSelector((state: RootState) => getCurrentValue2(state, 'ceilingTiles'));
  const context = useContext(ToolContext) as ToolContextType;
  const tile = context.constants.ceilingTiles[currentTiles.tile];
  const tileSize = mapValues(tile, (v: number) => v * scale);
  const tileShape = Math.sign(tileSize.x - tileSize.y); // returns -1 for vertical, 0 for square and 1 for horizontal
  const iconScale = Math.min(tileSize.x, tileSize.y) * .40;

  const [tileCenter, setTileCenter] = useState<TileCenter>({ x: 0, y: 0 });
  const [checked, setChecked] = useState<TileChecked[]>([]);
  const [ceilingTransform, setCeilingTransfrom] = useState<CeilingTransform>({ x: 0, y: 0 });
  const [iconTransform, setIconTransform] = useState<IconTransform>({ x: 0, y: 0, scale: 0 });
  const [moveLimits, setMoveLimits] = useState<MoveLimits>({ x: [0, 0], y: [0, 0] });
  const { drawTileQty, actualTileQty } = useSelector(getTileQuantities);
  // Full coverage = canonical selector added its +2 hidden-buffer ring (drawTileQty > actualTileQty).
  // Don't compare actualTileQty to maxDrawTileQty here — the selector returns
  // maxDrawTileQty = maxFit + 2 (i.e. the max VALUE of drawTileQty, not the max
  // tiles that fit), so that equality can never hold after clamping.
  const isFullCoverageX = drawTileQty.x > actualTileQty.x;
  const isFullCoverageY = drawTileQty.y > actualTileQty.y;
  // Store actualTileQty in a ref so it's available in event handlers
  const actualTileQtyRef = useRef(actualTileQty);
  useEffect(() => {
    actualTileQtyRef.current = actualTileQty;
  }, [actualTileQty]);
  const dispatch = useDispatch();
  const mouse = useMousePosition(true, true);
  const [offset, setOffset] = useState<OffsetPosition>({ x: 0, y: 0 });
  const blocking = useSelector((state: RootState) => getCurrentValue2(state, 'action') === 'block');
  const [moving, setMoving] = useState<boolean>(false);
  // Ref to track previous quantity to prevent infinite update loops
  const previousQtyRef = useRef(currentTiles.qty);

  // Simple effect to ensure component re-renders when tile type changes
  useEffect(() => {
    // Just ensure the component knows when tile type has changed
    // This will trigger the useLayoutEffect to recalculate positions
  }, [currentTiles.tile]);

  // This effect handles UI position calculations only
  useLayoutEffect(() => {
    try {
      // Safe guards for invalid inputs
      const safeDrawTileQtyX = Number.isFinite(drawTileQty.x) ? drawTileQty.x : 0;
      const safeDrawTileQtyY = Number.isFinite(drawTileQty.y) ? drawTileQty.y : 0;
      
      // Calculate the total width and height of the ceiling tiles grid
      const totalTileWidth = safeDrawTileQtyX * tileSize.x;
      const totalTileHeight = safeDrawTileQtyY * tileSize.y;
      
      // Calculate the center of the tile grid
      const cx = round(totalTileWidth / 2);
      const cy = round(totalTileHeight / 2);
      setTileCenter({ x: cx, y: cy });

      // Calculate the transformation to center the tiles in the room
      // bbox.cx and bbox.cy are the center of the room
      // Start with grid centered (buffer tiles extend equally on all sides)
      const tx = bbox.cx - cx + (currentTiles.transform.x || 0) * scale;
      const ty = bbox.cy - cy + (currentTiles.transform.y || 0) * scale;

      const ta = `${currentTiles.transform.a || 0} ${cx} ${cy}`;
      setCeilingTransfrom({ x: tx, y: ty, a: ta });

      // Get proper floorplan boundaries using the utility
      const boundaries = getFloorplanBoundaries(bbox, scale, false, 0);

      // Movement limits:
      //   - Full coverage (buffer present): ±1 tile from centered anchor, so the hidden
      //     buffer ring stays outside the room boundary as the grid shifts.
      //   - Partial coverage: slack between room and grid → user can drag wall to wall.
      const slackX = Math.max(0, bbox.width  - safeDrawTileQtyX * tileSize.x);
      const slackY = Math.max(0, bbox.height - safeDrawTileQtyY * tileSize.y);
      const movementX = isFullCoverageX ? tileSize.x : slackX / 2;
      const movementY = isFullCoverageY ? tileSize.y : slackY / 2;

      const centerX = bbox.cx - cx;
      const centerY = bbox.cy - cy;

      setMoveLimits({
        x: [centerX - movementX, centerX + movementX],
        y: [centerY - movementY, centerY + movementY]
      });

      // Anchor the move icon to the rightmost / topmost LOCAL tile that's still
      // fully inside the room. We recompute against `tx`/`ty` (and against the
      // dragged transform in onPointerMove) so as the grid shifts the icon stays
      // visible — pinning the icon to a fixed local tile sent it outside the
      // canvas once the grid moved by ~1 tile (see PIG-209).
      const iconAnchor = computeIconAnchor(tx, ty, tileSize, safeDrawTileQtyX, safeDrawTileQtyY, bbox);
      setIconTransform({ x: iconAnchor.x, y: iconAnchor.y, scale: 1.0 });
    } catch (e) {
      console.error("Error in ceiling calculation:", e);
    }
  }, [
    // Source values referenced by the effect body. `isFullCoverageX/Y` are listed
    // even though they're pure derivations of drawTileQty/actualTileQty so the
    // exhaustive-deps lint rule stays satisfied without a blanket disable.
    currentTiles.tile, currentTiles.transform,
    bbox, scale,
    tile, tileSize.x, tileSize.y,
    drawTileQty.x, drawTileQty.y,
    actualTileQty.x, actualTileQty.y,
    isFullCoverageX, isFullCoverageY,
  ]);
  
  // Instead of having an effect that updates Redux state based on derived values,
  // we'll move this logic to the reducer and let it handle the calculations.
  // This breaks the infinite update cycle that was happening.
  // The TileQuantities calculation is still done in the selector, but we don't
  // automatically dispatch from the component anymore.

  useEffect(() => {
    setChecked(currentTiles.checked);
  }, [currentTiles.checked]);

  const markHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      if (ceilingRef.current) {
        ceilingRef.current.setPointerCapture(e.pointerId);
        
        // Only process if we have valid mouse coordinates and tile sizes
        if (mouse && mouse.x && mouse.y && 
            Number.isFinite(tileSize.x) && tileSize.x > 0 && 
            Number.isFinite(tileSize.y) && tileSize.y > 0) {
            
          const idX = (mouse.x - 0 - ceilingTransform.x) / tileSize.x;
          const idY = (mouse.y - 0 - ceilingTransform.y) / tileSize.y;
          
          // Calculate which part of the tile was clicked
          // For non-square tiles, determine if click was on left/upper, center, or right/lower
          let mask = 0;
          if (tileShape !== 0) {
            const clickPosition = tileShape < 0 ? (idY % 1) : (idX % 1);
            if (clickPosition < 0.35) {
              // Clicked on left/upper side - mark with -1
              mask = -1;
            } else if (clickPosition > 0.65) {
              // Clicked on right/lower side - mark with 1
              mask = 1;
            } else {
              // Clicked in center - mark both sides with 0
              mask = 0;
            }
          }
          
          const tile = { x: Math.trunc(idX), y: Math.trunc(idY), mask: mask };
          
          try {
            // Use a safer way to clone the array to avoid potential issues
            const update: TileChecked[] = [...checked];
            // Only match on x and y coordinates, not mask
            const index = findIndex(checked, (item) => item.x === tile.x && item.y === tile.y);
            
            if (index >= 0) {
              const existing = update[index];
              
              if (tile.mask === 0) {
                // Center clicked - toggle both sides
                if (existing.mask === 0) {
                  // Both sides were blocked, unblock them
                  update.splice(index, 1);
                } else {
                  // One side was blocked, block both sides now
                  update[index] = { ...existing, mask: 0 };
                }
              } else {
                // A specific side was clicked
                if (existing.mask === 0) {
                  // Both sides were blocked, clicking a side unblocks only that side
                  update[index] = { ...existing, mask: -tile.mask };
                } else if (tile.mask === existing.mask) {
                  // Same side clicked again - unblock it
                  update.splice(index, 1);
                } else {
                  // Opposite side clicked when one side is already blocked
                  // This should block both sides
                  update[index] = { ...existing, mask: 0 };
                }
              }
            } else {
              // New tile - add it with the clicked mask
              update.push(tile);
            }
            
            setChecked(update);
          } catch (err) {
            console.error("Error updating ceiling tiles:", err);
          }
        }
      }
    },
    onPointerUp: (e: React.PointerEvent) => {
      try {
        if (ceilingRef.current) {
          ceilingRef.current.releasePointerCapture(e.pointerId);
        }
        
        dispatch({
          type: 'container/setCeilingTiles',
          payload: { checked: checked }
        });
      } catch (err) {
        console.error("Error handling pointer up event:", err);
      }
    }
  };

  const moveHandlers = {
    onPointerDown: (e: React.PointerEvent) => {
      try {
        if (ceilingIconRef.current) {
          ceilingIconRef.current.setPointerCapture(e.pointerId);
        }
        
        // Store initial offset between mouse and ceiling position using SVG coordinates
        if (mouse?.x && mouse?.y) {
          setOffset({ 
            x: mouse.x - ceilingTransform.x, 
            y: mouse.y - ceilingTransform.y 
          });
          
        }
        
        setMoving(true);
        dispatch(ReducersIndex.uiReducers.setAction('moveCeiling'));
        e.stopPropagation();
      } catch (err) {
        console.error("Error in move pointer down handler:", err);
      }
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (!moving) return;
      
      // Use the existing mouse hook for SVG coordinates instead of clientX/Y
      const { x: mouseX = 0, y: mouseY = 0 } = mouse ?? {};
      if (!mouseX || !mouseY) return;
      
      // Calculate new ceiling position based on mouse movement
      const newCeilingX = mouseX - offset.x;
      const newCeilingY = mouseY - offset.y;

      // Mirror the movement-limit math from useLayoutEffect:
      //   - Full coverage: ±1 tile from anchor; the +2 buffer ring absorbs the slack
      //     as the user drags so lines visibly shift within the room.
      //   - Partial coverage: ±slack/2 → wall-to-wall.
      // No whole-tile snap: a previous version snapped clampedX/Y to integer tile
      // multiples to "keep visible count == tileQty," but at full coverage that made
      // every snap position render at the same world coords (since lines are at
      // multiples of tileSize and shifting by tileSize re-aligns them) — the move
      // icon shifted but the grid looked frozen. Continuous movement lets the user
      // see the shift; a partial tile at one edge is the acceptable trade-off.
      const safeDrawTileQtyX = Number.isFinite(drawTileQty.x) ? drawTileQty.x : 0;
      const safeDrawTileQtyY = Number.isFinite(drawTileQty.y) ? drawTileQty.y : 0;
      const cx = (safeDrawTileQtyX * tileSize.x) / 2;
      const cy = (safeDrawTileQtyY * tileSize.y) / 2;
      const centerX = bbox.cx - cx;
      const centerY = bbox.cy - cy;
      const slackX = Math.max(0, bbox.width  - safeDrawTileQtyX * tileSize.x);
      const slackY = Math.max(0, bbox.height - safeDrawTileQtyY * tileSize.y);
      const movementX = isFullCoverageX ? tileSize.x : slackX / 2;
      const movementY = isFullCoverageY ? tileSize.y : slackY / 2;

      const clampedX = Math.max(centerX - movementX, Math.min(centerX + movementX, newCeilingX));
      const clampedY = Math.max(centerY - movementY, Math.min(centerY + movementY, newCeilingY));
      
      // Update ceiling transform - this should move the entire grid.
      setCeilingTransfrom({
        x: clampedX,
        y: clampedY,
        a: ceilingTransform.a // Preserve rotation
      });

      // Re-anchor the icon against the new grid position so it stays inside
      // the room as the user drags. Without this the icon (pinned to a fixed
      // local tile) drifts outside the canvas once the grid moves ~1 tile.
      const iconAnchor = computeIconAnchor(clampedX, clampedY, tileSize, safeDrawTileQtyX, safeDrawTileQtyY, bbox);
      setIconTransform({ x: iconAnchor.x, y: iconAnchor.y, scale: 1.0 });
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (ceilingIconRef.current) {
        ceilingIconRef.current.releasePointerCapture(e.pointerId);
      }
      setMoving(false);

      dispatch(ReducersIndex.uiReducers.setAction());

      // Calculate the transform for saving
      const x = round((ceilingTransform.x - bbox.cx + tileCenter.x) / scale, 4);
      const y = round((ceilingTransform.y - bbox.cy + tileCenter.y) / scale, 4);

      dispatch({
        type: 'container/setCeilingTiles',
        payload: { transform: { x, y, a: 0 } }
      });
    }
  };

  const { model: micModel, layout: micLayout } = useSelector(getMicrophoneInfo);
  const active = useSelector((state: RootState) => getConditionResult(state, { ...props.active, area: areaId }));
  const visible = useSelector((state: RootState) => getConditionResult(state, { ...props.show, area: areaId }));
  
  const ceilingIconRef = useRef<SVGGElement>(null);
  const ceilingRef = useRef<SVGGElement>(null);

  // Safety check for bbox properties
  const safeBbox = {
    x0: Number.isFinite(bbox?.x0) ? bbox.x0 : 0,
    y0: Number.isFinite(bbox?.y0) ? bbox.y0 : 0,
    width: Number.isFinite(bbox?.width) ? bbox.width : 0,
    height: Number.isFinite(bbox?.height) ? bbox.height : 0,
    cx: Number.isFinite(bbox?.cx) ? bbox.cx : 0,
    cy: Number.isFinite(bbox?.cy) ? bbox.cy : 0,
    x1: Number.isFinite(bbox?.x1) ? bbox.x1 : 0
  };

  // Tile highlights for CEILING_TILE mics (BMA360D) — light blue fill on the occupied tile
  const hasTiles = currentTiles.tile !== "no_ceiling_tiles" && tileSize.x > 0 && tileSize.y > 0;
  const isCeilingTileMic = hasTiles && micInCategory(micModel, 'CEILING_TILE');
  // PIG-526: the highlight must match the mic's 2'×2' footprint (the same square the icon fills),
  // NOT the full ceiling tile. For 2'×4' / 4'×2' tiles the old code used tileSize.x/y and painted
  // the whole tile, so the blue layer overhung the square icon. Use the mic's physicalDimensions
  // (the source the icon scale uses) so the two always agree; fall back to the shorter tile side
  // (which equals the footprint for the supported tile sizes) if the config is missing.
  const micFootprintMeters = (context.constants.equipment?.microphones?.[micModel] as any)?.physicalDimensions?.x;
  const micFootprint = micFootprintMeters ? micFootprintMeters * scale : Math.min(tileSize.x, tileSize.y);
  const tileHighlights = isCeilingTileMic
    ? micLayout
        .filter((mic: any) => mic?.location?.x != null && mic?.location?.y != null)
        .map((mic: any) => ({
          // Centre the footprint square on the mic (group-local coords), matching the icon.
          x: safeBbox.x0 + mic.location.x * scale - ceilingTransform.x - micFootprint / 2,
          y: safeBbox.y0 + mic.location.y * scale - ceilingTransform.y - micFootprint / 2,
        }))
    : [];

  return (
    <>
      {visible && (
        <>
          <CeilingGroup
            id={`${areaId}-Ceiling`}
            ref={ceilingRef}
            active={active}
            bboxX0={safeBbox.x0}
            bboxY0={safeBbox.y0}
            bboxWidth={safeBbox.width}
            bboxHeight={safeBbox.height}
            tx={ceilingTransform}
            clipX={isFullCoverageX}
            clipY={isFullCoverageY}
            {...(blocking ? markHandlers : {})}
          >
            {tileHighlights.map((pos, i) => (
              <rect
                key={`tile-highlight-${i}`}
                x={pos.x}
                y={pos.y}
                width={micFootprint}
                height={micFootprint}
                fill="rgba(59, 130, 246, 0.15)"
                stroke="rgba(59, 130, 246, 0.4)"
                strokeWidth={1}
                pointerEvents="none"
              />
            ))}
            {(() => {
              // Create a safe array to ensure we never hit invalid array length errors
              const safeCreateArray = (size: number) => {
                // Ensure size is a valid, finite, non-negative integer
                if (typeof size !== 'number' || !isFinite(size) || size < 0) {
                  return [];
                }
                
                // Safely create array with proper size (capped at 1000 for protection)
                const length = Math.min(Math.floor(size), 1000);
                try {
                  return Array(length).fill(undefined);
                } catch (e) {
                  console.error('Failed to create array:', e);
                  return [];
                }
              };
              
              // Create vertical lines
              const xCount = Number.isFinite(drawTileQty.x) ? Math.floor(drawTileQty.x) + 1 : 0;
              return safeCreateArray(xCount).map((_, x) => (
                // Vertical grid line. +0.5 puts the 1px stroke on a pixel boundary (crisp)
                // now that the group translate is integer-snapped; extent rounded to whole px.
                <CeilingLine
                  key={`ver-${x}`}
                  active={active}
                  d={`m ${Math.round(tileSize.x * x) + 0.5} 0 v${Math.round((Number.isFinite(drawTileQty.y) ? drawTileQty.y : 0) * tileSize.y)}`}
                />
              ));
            })()}
            
            {(() => {
              // Create a safe array to ensure we never hit invalid array length errors
              const safeCreateArray = (size: number) => {
                // Ensure size is a valid, finite, non-negative integer
                if (typeof size !== 'number' || !isFinite(size) || size < 0) {
                  return [];
                }
                
                // Safely create array with proper size (capped at 1000 for protection)
                const length = Math.min(Math.floor(size), 1000);
                try {
                  return Array(length).fill(undefined);
                } catch (e) {
                  console.error('Failed to create array:', e);
                  return [];
                }
              };
              
              // Create horizontal lines
              const yCount = Number.isFinite(drawTileQty.y) ? Math.floor(drawTileQty.y) + 1 : 0;
              return safeCreateArray(yCount).map((_, y) => (
                <CeilingLine
                  key={`hor-${y}`}
                  active={active}
                  d={`m 0 ${Math.round(tileSize.y * y) + 0.5} h${Math.round((Number.isFinite(drawTileQty.x) ? drawTileQty.x : 0) * tileSize.x)}`}
                />
              ));
            })()}
            
            {checked.map((ck, id) => {
              const x = 0 + ck.x * tileSize.x;
              const y = 0 + ck.y * tileSize.y;
              const side = round((Math.min(tileSize.x, tileSize.y) - 4) * 2) / 2;
              return (
                <Fragment key={`frag-${id}`}>
                  {(tileShape === 0 || ck.mask <= 0) && (
                    <CeilingCross 
                      key={`cross-${id}-1`} 
                      d={`m ${x + 2} ${y + 2} l ${side} ${side} m 0 -${side} l -${side} ${side}`}
                      active={active} 
                    />
                  )}
                  {(tileShape !== 0 && ck.mask >= 0) && (
                    <CeilingCross 
                      key={`cross-${id}-2`} 
                      d={`m ${x + (tileShape > 0 ? side + 2 : 0) + 2} ${y + (tileShape < 0 ? side + 2 : 0) + 2} l ${side} ${side} m 0 -${side} l -${side} ${side}`}
                      active={active} 
                    />
                  )}
                </Fragment>
              );
            })}
          </CeilingGroup>
          
          {/* Move icon outside the clipped group to ensure visibility */}
          {active && visible && (
            <CeilingMove 
              ref={ceilingIconRef} 
              transform={`translate(${ceilingTransform.x + iconTransform.x}, ${ceilingTransform.y + iconTransform.y}) scale(${iconTransform.scale})`}
              pointer={moving} 
              {...moveHandlers} 
            >
              <Move state={{
                'border_stroke': '#3b82f6',
                'border_fill': '#ffffff', 
                'icon_stroke': '#3b82f6',
                'icon_fill': 'none',
                border: '2px solid #3b82f6',
              }} center />
            </CeilingMove>
          )}
        </>
      )}
    </>
  );
};


// Local interface for styled component to ensure type safety
interface StyledCeilingGroupProps {
  active: boolean;
  tx: CeilingTransform;
  bboxX0?: number;
  bboxY0?: number;
  bboxWidth?: number;
  bboxHeight?: number;
  clipX?: boolean;
  clipY?: boolean;
}

// Styled components using MUI styled API
const CeilingGroup = styled('g', {
  shouldForwardProp: (prop) => !['active', 'tx', 'bboxX0', 'bboxY0', 'bboxWidth', 'bboxHeight', 'clipX', 'clipY'].includes(prop as string),
})<StyledCeilingGroupProps>`
  /* Snap the grid translate to whole device pixels so the 1px tile strokes render crisp.
     A fractional translate smears each line across two pixel rows (the faint "double /
     dotted / blurry" look) and defeats shape-rendering: crispEdges. The clip-path uses the
     same rounded value so it stays aligned with the transform. */
  //transform: translate(${({ tx }) => Math.round(tx.x)}px, ${({ tx }) => Math.round(tx.y)}px);
  transform: translate(${({ tx }) => tx.x}px, ${({ tx }) => tx.y}px);
  stroke-width: 1;
  shape-rendering: crispEdges;
  pointer-events: ${({ active }) => active ? 'bounding-box' : 'none'};
  /* Clip the grid to the room PER AXIS, only on the axis that has a buffer to hide. The +2 hidden
     buffer ring is added per axis, at FULL coverage on that axis, and is drawn a tile past each
     wall — so that axis must be clipped to the room. A PARTIAL axis has NO buffer and is always
     inside the room (the drag clamp keeps its edge at/inside the wall); clipping it only shaves the
     boundary tile line (whose Math.round(tileSize*col)+0.5 overshoot varies with grid position, so
     the shaved amount changes as the grid moves). So a partial axis is left un-clipped by extending
     the clip rect a full room-dimension past each side on that axis (the in-room grid never reaches
     it). This is why a 10×10 grid in a 20-tall room (partial X, full Y) must clip Y but NOT X —
     otherwise moving it sideways shaves the X edge. Aligns to the SAME un-rounded tx as the
     transform above so clip and grid don't drift. */
  clip-path: ${({ clipX, clipY, tx, bboxX0 = 0, bboxY0 = 0, bboxWidth = 0, bboxHeight = 0 }) => {
    const sx = clipX ? 0 : bboxWidth;   // partial X → extend a room-width each side (no X clip)
    const sy = clipY ? 0 : bboxHeight;  // partial Y → extend a room-height each side (no Y clip)
    return `path('m ${bboxX0 - tx.x - sx}, ${bboxY0 - tx.y - sy} h ${bboxWidth + 2 * sx} v ${bboxHeight + 2 * sy} h${-(bboxWidth + 2 * sx)} z')`;
  }};
`;

const CeilingLine = styled('path', {
  shouldForwardProp: (prop) => prop !== 'active',
})<CeilingLineProps>(({ theme, active }) => ({
  stroke: active ? '#6b7280' : '#d1d5db',  // Consistent grey colors
  strokeWidth: active ? '1.5px' : '1px',
  strokeOpacity: active ? 1 : 0.6,
  shapeRendering: 'crispEdges',
}));

const CeilingCross = styled('path', {
  shouldForwardProp: (prop) => prop !== 'active',
})<CeilingCrossProps>(({ theme, active }) => ({
  stroke: active ? '#6b7280' : '#d1d5db',  // Consistent grey colors for crosses
  strokeWidth: active ? '2px' : '1.5px',
  strokeOpacity: active ? 1 : 0.8,
  shapeRendering: 'crispEdges',
}));

const CeilingMove = styled('g', {
  shouldForwardProp: (prop) => prop !== 'pointer',
})<CeilingMoveProps>`
  cursor: ${({ pointer }) => pointer ? 'all-scroll' : 'pointer'};
  &:hover {
    cursor: pointer;
  }
`;

export default MuiCeiling;
